import axios from 'axios';
import { API_CONFIG } from '../config/api.config';
import { LoginCredentials, AuthResponse } from '../types/auth';
import { store } from '../store/store';
import { setUser, logOut } from '../store/slices/authSlice';
import { startLoading, stopLoading } from '../store/slices/loadingSlice';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import useAuthStore from '../store/authStore';

/** True if the user has admin role (id 1), including multi-role API shapes. */
export function isAdminReduxRole(roles: string | undefined): boolean {
  if (roles == null || roles === '') return false;
  const s = String(roles).trim();
  if (s === '1') return true;
  if (s.includes(',')) {
    return s.split(',').map((x) => x.trim()).includes('1');
  }
  return s.toLowerCase() === 'admin';
}

/**
 * Flatten login/verify/JWT `roles` into a list of entries (numeric ids or name strings).
 * Handles arrays, single numbers, comma-separated strings, JSON string arrays, and
 * array-like objects ({ "0": 1 }) which some JWT stacks produce.
 */
export function normalizeRolesPayload(raw: unknown): unknown[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'number' || typeof raw === 'bigint') return [raw];
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t) as unknown;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        /* fall through */
      }
    }
    return t.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if ('role_id' in o && o.role_id != null) return [o.role_id];
    if (Array.isArray(o.roles)) return o.roles as unknown[];
    const vals = Object.values(o);
    if (
      vals.length > 0 &&
      vals.every((v) => typeof v === 'number' || typeof v === 'string' || typeof v === 'bigint')
    ) {
      return vals;
    }
  }
  return [];
}

function entryIsAdminRole(r: unknown): boolean {
  if (typeof r === 'bigint') return r === BigInt(1);
  if (typeof r === 'number') return r === 1;
  const t = String(r).toLowerCase().trim();
  return t === '1' || t === 'admin';
}

/** Map API role names / ids to Redux role flag "1" | "2". */
export function mapRoleNamesToReduxRole(roles: unknown): string {
  const list = normalizeRolesPayload(roles);
  if (list.length === 0) return '2';
  const hasAdmin = list.some(entryIsAdminRole);
  return hasAdmin ? '1' : '2';
}

/**
 * Build Redux user from verifyAccess payload. If `roles` is omitted (some legacy
 * servers only return ids + company), keep the previous session roles so login →
 * verify does not downgrade admins to viewers.
 */
export function reduxUserFromVerifyPayload(d: {
  userID: number;
  companyID: number | string;
  companyName: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roles?: unknown;
}) {
  const prev = store.getState().auth.user;
  const rolesProvided =
    d != null &&
    Object.prototype.hasOwnProperty.call(d, 'roles') &&
    d.roles !== undefined &&
    d.roles !== null;
  const rawRoles = rolesProvided ? d.roles : prev?.roles;
  return {
    id: d.userID,
    companyID: String(d.companyID),
    companyName: d.companyName,
    email: d.email ?? prev?.email,
    firstName: d.firstName ?? prev?.firstName,
    lastName: d.lastName ?? prev?.lastName,
    roles: mapRoleNamesToReduxRole(rawRoles),
  };
}

// Create separate instances for auth and data APIs
const authAxios = axios.create({
  baseURL: 'https://auth.ts1cloud.com',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
  timeout: 30000
});

// Retry on 500/503 — Cloudflare returns 500 when a cold Worker isolate is killed before responding.
// One automatic retry after a short delay is enough for Hyperdrive to warm up.
authAxios.interceptors.response.use(undefined, async (err) => {
  const config = err.config;
  const status = err.response?.status;
  if (status !== 500 && status !== 503) return Promise.reject(err);
  config._retryCount = (config._retryCount || 0) + 1;
  if (config._retryCount > 1) return Promise.reject(err);
  await new Promise(resolve => setTimeout(resolve, 600));
  return authAxios(config);
});

const dataApi = axios.create({
  ...API_CONFIG,
  retry: 3,
  retryDelay: (retryCount) => retryCount * 1000
});

// Add request interceptor to set company headers
dataApi.interceptors.request.use((config) => {
  const state = store.getState();
  const user = state.auth.user;

  if (user?.companyID && user?.companyName) {
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['x-company-id'] = user.companyID.toString();
    config.headers['x-company-name'] = user.companyName;
    config.headers['Origin'] = window.location.origin;
  } else {
    console.warn('Missing company information in user data');
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add retry interceptor for data API
dataApi.interceptors.response.use(undefined, async (err) => {
  const config = err.config;
  if (!config || !config.retry) return Promise.reject(err);
  
  config.retryCount = config.retryCount || 0;
  
  if (config.retryCount >= config.retry) {
    return Promise.reject(err);
  }
  
  config.retryCount += 1;
  const delayRetry = new Promise(resolve => setTimeout(resolve, config.retryDelay(config.retryCount)));
  await delayRetry;
  return dataApi(config);
});

// Session timeout handling
let sessionTimeoutId: number;
let verificationIntervalId: number;
let idleTimeoutId: number | null = null;
let idleWarningId: number | null = null;

const SESSION_DURATION = 24 * 60 * 60 * 1000;
const VERIFICATION_INTERVAL = 30 * 60 * 1000;
const IDLE_TIMEOUT = 30 * 60 * 1000;
const IDLE_WARNING_BEFORE = 2 * 60 * 1000;

const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart'] as const;

const resetIdleTimeout = () => {
  if (idleWarningId) window.clearTimeout(idleWarningId);
  if (idleTimeoutId) window.clearTimeout(idleTimeoutId);
  idleWarningId = window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('auth:idle-warning'));
  }, IDLE_TIMEOUT - IDLE_WARNING_BEFORE);
  idleTimeoutId = window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('auth:timeout'));
  }, IDLE_TIMEOUT);
};

const startIdleDetection = () => {
  IDLE_EVENTS.forEach(e => document.addEventListener(e, resetIdleTimeout, { passive: true }));
  resetIdleTimeout();
};

const stopIdleDetection = () => {
  IDLE_EVENTS.forEach(e => document.removeEventListener(e, resetIdleTimeout));
  if (idleWarningId) window.clearTimeout(idleWarningId);
  if (idleTimeoutId) window.clearTimeout(idleTimeoutId);
  idleWarningId = null;
  idleTimeoutId = null;
};

// Add loading state management to data API
dataApi.interceptors.request.use(config => {
  store.dispatch(startLoading());
  
  const state = store.getState();
  const user = state.auth.user;

  if (user?.companyID && user?.companyName) {
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['x-company-id'] = user.companyID.toString();
    config.headers['x-company-name'] = user.companyName;
    config.headers['Origin'] = window.location.origin;
  }

  return config;
});

dataApi.interceptors.response.use(
  (response) => {
    store.dispatch(stopLoading());
    return response;
  },
  async (error) => {
    store.dispatch(stopLoading());
    if (error.response?.status === 401) {
      try {
        const response = await authApi.verifyAccess();
        if (response.message === 'Valid Session' && response.data) {
          startSessionTimeout();
          const d = response.data;
          if (d?.userID) {
            store.dispatch(setUser(reduxUserFromVerifyPayload(d)));
          } else {
            console.warn('verifyAccess returned user data without userID, not updating user data');
          }
          return dataApi(error.config);
        }
      } catch (verifyError) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }

    if (error.response?.status === 403) {
      window.dispatchEvent(new CustomEvent('auth:forbidden'));
    }

    return Promise.reject(error);
  }
);

export const startSessionTimeout = () => {
  clearSessionTimeout();
  sessionTimeoutId = window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('auth:timeout'));
  }, SESSION_DURATION);

  verificationIntervalId = window.setInterval(async () => {
    try {
      await authApi.verifyAccess();
    } catch (error) {
      console.error('Session verification failed:', error);
      window.dispatchEvent(new CustomEvent('auth:timeout'));
    }
  }, VERIFICATION_INTERVAL);

  startIdleDetection();
};

export const clearSessionTimeout = () => {
  if (sessionTimeoutId) clearTimeout(sessionTimeoutId);
  if (verificationIntervalId) clearInterval(verificationIntervalId);
  stopIdleDetection();
};

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await authAxios.post<AuthResponse>('/auth/login', credentials);
      startSessionTimeout();
      
      // Map user data consistently
      const userData = {
        id: response.data.userData.userID,
        companyID: response.data.userData.companyID.toString(),
        companyName: response.data.userData.companyName,
        email: response.data.userData.email,
        firstName: response.data.userData.firstName,
        lastName: response.data.userData.lastName,
        roles: mapRoleNamesToReduxRole(response.data.userData.roles),
      };
      
      store.dispatch(setUser(userData));
      
      // Also update Zustand store with correct types
      const zustandUserData = {
        id: response.data.userData.userID,
        companyID: response.data.userData.companyID,
        companyName: response.data.userData.companyName,
        email: response.data.userData.email,
        firstName: response.data.userData.firstName,
        lastName: response.data.userData.lastName,
        roles: Array.isArray(response.data.userData.roles)
          ? response.data.userData.roles
          : [String(response.data.userData.roles)],
      };
      useAuthStore.getState().setUser(zustandUserData);
      
      // Log successful login after user is set in store
      try {
        await logAuditEvent(
          AUDIT_ACTIONS.USER_LOGIN, 
          `User logged in via password - Company: ${userData.companyName}, Email: ${userData.email}`,
          userData
        );
      } catch (auditError) {
        console.error('Failed to log audit event:', auditError);
      }
      
      return response.data;
    } catch (error) {
      console.error('Login Error:', error);
      throw error;
    }
  },
  
  logout: async () => {
    try {
      clearSessionTimeout();
      await authAxios.post('/auth/logout', {}, { withCredentials: true });
      
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear Redux store
      store.dispatch(logOut());
      
      // Clear Zustand store
      useAuthStore.getState().logout();
      
      return { success: true };
    } catch (error) {
      console.error('Logout Error:', error);
      // Even if the API call fails, we should still clear local state
      localStorage.clear();
      sessionStorage.clear();
      store.dispatch(logOut());
      useAuthStore.getState().logout();
      throw error;
    }
  },
  
  verifyAccess: async () => {
    try {
      const response = await authAxios.get('/auth/verifyAccess', {
        withCredentials: true
      });
      
      if (response.data?.message === 'Valid Session' && response.data?.data) {
        startSessionTimeout();

        const d = response.data.data;
        const userData = reduxUserFromVerifyPayload(d);
        store.dispatch(setUser(userData));

        const rolesProvided =
          d != null &&
          Object.prototype.hasOwnProperty.call(d, 'roles') &&
          d.roles !== undefined &&
          d.roles !== null;
        const rawForZustand = rolesProvided ? d.roles : userData.roles;
        const zustandUserData = {
          id: d.userID,
          companyID: d.companyID,
          companyName: d.companyName,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          roles: normalizeRolesPayload(rawForZustand).map(String),
        };
        useAuthStore.getState().setUser(zustandUserData);
      }
      
      return response.data;
    } catch (error) {
      console.error('Verify Access Error:', error);
      throw error;
    }
  },
  
  updatePassword: async (companyName: string, email: string, currentPassword: string, newPassword: string) => {
    try {
      const response = await authAxios.post('/auth/updatePassword', {
        companyName,
        email,
        currentPassword,
        newPassword
      }, {
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }
};

// Export the data API for other services
export const api = dataApi;